/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2021-06-10 18:10:18
 *
 *******************************************************/
//@flow
import _ from 'lodash';

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Banner } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import history from 'src/routes/history';

import { Carousel } from 'src/components/views/common/carousel';
import SpecialDiscountExpiredModal from 'src/components/containers/common/notificationSubscription/special-discount-notification';
import InvalidMetafieldsFound from 'src/components/views/tools/metafield/invalid-metafields-found';
import ExceededPlanNotifications from 'src/components/containers/common/exceeded-plan-notifications';

import { trackClick } from 'src/helpers/mixpanel-helper';
import { getShop, createMarkup, getFirstCharacter } from 'src/helpers';

import * as notificationAction from 'actions/dashboard/notification-actions';
import * as metafieldActions from 'src/stores/actions/metafield-actions';

type Props = {
  notifications: Array<Object>,
  dismissNotification: Function,
  emptyMetafields: Array<any>,
  invalidMetafields: Array<any>,
  emailSupport: string,
  themesLoading: boolean,
  existedThemeLibV2: boolean,
  getMetafields: Function,
  subscription: Object
};

type State = {
  dismissList: Array<string>,
  width: number
};

type ComposedProps = Props & WithI18nProps;

class Notifications extends Component<ComposedProps, State> {
  state = {
    dismissList: [],
    width: 0
  };

  componentDidMount() {
    window.addEventListener('resize', this.resizeWindowEvent);

    this.props.getMetafields();
    // Get dismissList from local storage
    // Check show info banner
    let dismissList = [];
    try {
      dismissList = JSON.parse(
        localStorage.getItem(`${getShop()}-notificationDismissList`) || '[]'
      );
    } catch (error) {}

    this.setState({ dismissList });
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resizeWindowEvent);
  }

  resizeWindowEvent = () => {
    this.setState({ width: window.innerWidth });
  };

  render() {
    const { notifications = [] } = this.props;
    const { dismissList } = this.state;

    const filteredNotifications = _.filter(notifications, item => !dismissList.includes(item.id));

    const items = _.map(filteredNotifications, (item, index) =>
      this.renderNotification(item, index)
    );
    this.renderCustomNotification(items);

    if (items.length === 0) return '';
    return (
      <div className="BC-Notifications_DashboardV3">
        {/* <SystemNotifications /> */}
        {items.length === 1 && <div className="notification-item">{items[0]}</div>}
        {items.length > 1 && (
          <Carousel
            autoHeight={true}
            // alwaysShowSlide={true}
            items={[...items]}
          />
        )}
      </div>
    );
  }

  renderCustomNotification = (items = []) => {
    const { subscription, invalidMetafields, emptyMetafields } = this.props;

    const errorMetafields = [].concat(invalidMetafields, emptyMetafields);
    const { status: subscriptionStatus, afterDayDiscountExpired = '', discountCode } = subscription;
    const recentCodeApplied: string =
      _.get(discountCode, 'code', null) || _.get(discountCode, 'recentCodeApplied', '');

    // Add notif for invalid metafields
    if (errorMetafields.length > 0) {
      items.push(
        <div className="notification">
          <InvalidMetafieldsFound
            invalidMetafields={invalidMetafields}
            emptyMetafields={emptyMetafields}
            hasAction={true}
          />
        </div>
      );
    }

    // Add notify exceeded plan
    if (subscriptionStatus === 'exceededWithNotification') {
      items.push(
        <div className="notification">
          <ExceededPlanNotifications />
        </div>
      );
    }
    if (
      subscriptionStatus.includes('specialDiscount') ||
      (afterDayDiscountExpired.includes('specialDiscount') &&
        !recentCodeApplied.includes('ANALYTIC'))
    ) {
      items.unshift(
        <div className="notification">
          {/* Sticky notification for special discount code */}
          <SpecialDiscountExpiredModal />
        </div>
      );
    }
  };

  renderNotification = (notification, index) => {
    const { dismissList } = this.state;
    const { i18n } = this.props;

    const { contents = {}, type = 'information', allowDismiss = false, id = '' } = notification;
    const dismissIndex = _.findIndex(dismissList, item => item === id);

    if (_.isEmpty(contents) || dismissIndex !== -1) {
      return '';
    }

    const { buttonCTALink, buttonLabel, description, title, useButtonCTA } = contents;

    const propAction =
      useButtonCTA && !_.isEmpty(buttonCTALink)
        ? { action: this.buildAction(buttonCTALink, buttonLabel) }
        : {};
    const status = {
      information: 'info',
      warning: 'warning',
      error: 'critical',
      success: 'success'
    };

    const useDismiss = allowDismiss || allowDismiss === undefined;

    return (
      <div className="notification" key={index}>
        <Banner title={title} status={status[type]} {...propAction}>
          <>
            <p dangerouslySetInnerHTML={createMarkup(description)}></p>
            {_.isEmpty(propAction) && <br />}
            {useDismiss && (
              <div
                className={`BC-Notification_Dismiss BC-Notification_Dismiss${
                  _.isEmpty(propAction) ? '_WithoutActionButton' : '_WithActionButton'
                }`}
                onClick={() => {
                  this.onDismiss(id);
                }}>
                {i18n.translate('AdminHTML.Common.Action.dismiss')}
              </div>
            )}
          </>
        </Banner>
      </div>
    );
  };

  buildAction(action, actionLabel) {
    if (_.isEmpty(action)) return {};

    if (getFirstCharacter(action) === '/') {
      return {
        onAction: () => {
          trackClick(actionLabel);
          history.push(action);
        },
        content: actionLabel
      };
    }

    return {
      url: action,
      content: actionLabel,
      external: true,
      onAction: () => trackClick(actionLabel)
    };
  }

  onDismiss = (notificationId: string) => {
    const { dismissList } = this.state;
    dismissList.push(notificationId);
    try {
      localStorage.setItem(`${getShop()}-notificationDismissList`, JSON.stringify(dismissList));
    } catch (error) {}
    this.setState({ dismissList });
  };
}

const mapStateToProps = (state, props) => {
  return {
    notifications: _.get(state.dashboard, 'dashboardComponents.notifications.data', []),
    metafields: _.get(state.metafield, 'metafields', []),
    invalidMetafields: _.get(state.metafield, 'invalidMetafields', []),
    emptyMetafields: _.get(state.metafield, 'emptyMetafields', []),
    subscription: _.get(state.oauth, 'subscription', {}),
    feedback5StarsNextTimeClicks: _.get(
      state.oauth,
      'tenantApp.feedbackAfterRating5Stars.nextTimeClicks',
      0
    ),
    feedback5StarsIsDismissOptinNotify: _.get(
      state.oauth,
      'tenantApp.feedbackAfterRating5Stars.isNotShowOptinNotifyForever',
      false
    )
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  dismissNotification: notificationId =>
    dispatch(notificationAction.dismissNotification(notificationId)),
  getMetafields: () => dispatch(metafieldActions.getMetafields(ownProps.i18n))
});

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(Notifications));
